import { difference, union } from 'lodash-es';

import {
	checkPageGroupsPagesDuplicates,
	getIncomingParent
} from '@editor2/services/conflictResolutionService';
import { type Conflict, type ReorderConflict } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { NgTraditional } from '@editor2/types/ngTraditional';
import {
	handleDeletedInAcceptCurrentReorder,
	handleDeletedInAcceptIncomingPagesReorder,
	handleNewInAcceptCurrentReorder,
	handleNewInAcceptIncomingPagesReorder
} from '../common/conflictResolutionHelper';
import { getParentEntityByPageId } from '@editor2/components/CourseChangesDiff/helpers';
import { ChangesStatus } from '@editor2/types/changesStatus';

export class PageGroupChildrenReorderConflictResolver {
	acceptCurrent = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedTo = difference(initial, incoming);
		const addedOrMovedFrom = difference(incoming, current);

		state.initialCourse.page_groups.entities[entityId].pages = incoming;
		state.page_groups.entities[entityId].pages = current;

		const childEntityType = NgTraditional.Page;

		const newPayload = { state, addedOrMovedFrom, childEntityType };
		handleNewInAcceptCurrentReorder(newPayload);

		const deletedPayload = { state, deletedOrMovedTo, childEntityType, attribute };
		handleDeletedInAcceptCurrentReorder(deletedPayload);

		checkPageGroupsPagesDuplicates(state, entityId);
	};

	acceptIncoming = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		// 1 - discard current changes
		// 2 - apply incoming changes
		const { entityId, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedTo = difference(initial, current);
		const addedOrMovedFrom = difference(current, initial);

		handleNewInAcceptIncomingPagesReorder({ state, addedOrMovedFrom });
		handleDeletedInAcceptIncomingPagesReorder({ state, deletedOrMovedTo });

		state.initialCourse.page_groups.entities[entityId].pages = incoming;
		state.page_groups.entities[entityId].pages = incoming;

		checkPageGroupsPagesDuplicates(state, entityId);
	};

	acceptBoth = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedCurrent = difference(initial, current);
		const deletedIncoming = difference(initial, incoming);
		const merged = union(current, incoming);

		deletedCurrent.map((pageId) => {
			const currentParent =
				getParentEntityByPageId(state.chapters, pageId) ||
				getParentEntityByPageId(state.page_groups, pageId);
			if (!currentParent) {
				state.pages[pageId] = state.initialCourse.pages.entities[pageId];
				state.changes.pages[pageId] = [];
			}
		});
		deletedIncoming.map((pageId) => {
			const incomingParent = getIncomingParent({ attribute, id: pageId, state });
			if (!incomingParent) {
				state.changes.pages[pageId] = [ChangesStatus.New];
			}
		});

		state.initialCourse.page_groups.entities[entityId].pages = incoming;
		state.page_groups.entities[entityId].pages = merged;

		checkPageGroupsPagesDuplicates(state, entityId);
	};
}
